import { useCallback, useMemo, useState } from 'react';
import { ColumnITLayoutTableConfiguration, ITComponentElement, ITLayoutTableModalOpen, OnChangeElements } from '../../../../../baseComponents/TwinTable/InputTableLayout/InputTable/types';
import { closestCenter, DndContext, DragEndEvent, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { TwinDictionary } from '../../../../../utils/globals/dictionary';
import { flushSync } from 'react-dom';
import { twinFetchPostJSON } from '../../../../../utils/globals/data';
import { TableWithoutVirtualListingContainerRenderer, TableWithoutVirtualListingRowProps } from '../../../../../baseComponents/TwinTable/TableWithoutVirtualListing/types';
import { ITSortableItem } from '../../../../../baseComponents/TwinTable/InputTableLayout/InputTable/Components/Sort';
import { ColumnConfiguration } from '../../../../../baseComponents/TwinTable/VirtualTableListing/Subcomponents/types';
import { ITDeleteRow } from '../../../../../baseComponents/TwinTable/InputTableLayout/InputTable/Components/Displays';
import { useOnChangeOrderTableLogic } from '../../OrderLayout/OrderTable/useOrderTableHooks';
import { TableWithoutDefaultRowRenderer } from '../../../../../baseComponents/TwinTable/TableWithoutVirtualListing';
import { ITHookWSFunctionTy } from '../types';
import { WorkSheetProductStatusTypeInversed } from '@teinor/erp/types/company/order/sell/workSheet/workSheetProductStatus';
import { useSelectOrderTableLogic } from '../../OrderLayout/OrderTable/TableComponents/useSelectOrderTableLogic';
import { getFilteredTableDataLength } from '../../OrderLayout/OrderTable/useOrderLayoutTableLogic/functions';
import Checkbox from '../../../../../forms/Checkbox';
import useInputTableFilterFnc from '../../../../../utils/hooks/orders/useInputTableFilterFnc';

export const useWorkSheetLayoutTableLogic: ITHookWSFunctionTy = ({ rowRenderer, components, columns, CurrencyId, setCompleteData, inputTableURLs, getCompleteData, completeData, ...rest }) => {
    const [openModal, setOpenModal] = useState<ITLayoutTableModalOpen>(null)
    const [activeNChildren, setActiveNChildren] = useState<number | null>(null)
    const { onChangeSearchBar, changeSingleField } = useInputTableFilterFnc({ setCompleteData })
    const { onChange, deleteLine, onChangeFetch } = useOnChangeOrderTableLogic({ setCompleteData, inputTableURLs, getCompleteData, completeData, ...rest })
    const { changeSelectedOneLine, selectAllLines, selectedLength } = useSelectOrderTableLogic({ setCompleteData, completeData})
    const filteredTableDataLength = getFilteredTableDataLength(completeData)
    
    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                delay: 50,
                tolerance: 50
            }
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates
        })
    )

    const addExtraProps = useCallback((props: TableWithoutVirtualListingRowProps) => {
        let className = (props?.className || ' row_single_product ')
        if (props?.rowData?.['WorkSheetProductStatus.typeStatus'] === WorkSheetProductStatusTypeInversed['canceled']) {
            className += ' bg-red-FE'
        } else if (props?.rowData?.['WorkSheetProductStatus.typeStatus'] === WorkSheetProductStatusTypeInversed['ready']) {
            className += ' bg-green-F2'
        } 
        return {className}
    }, [])

    const handleProductLine = useCallback((props: TableWithoutVirtualListingRowProps) => {
        const { className } = addExtraProps(props)
        return (
            <TableWithoutDefaultRowRenderer {...props} className={className} key={props.rowData?.id || props.rowIndex} />
        )
    }, [addExtraProps])

    const handleOnDragEnd = useCallback(async (event: DragEndEvent) => {
        const { active, over } = event
        if (over && active.id !== over.id) {
            let worksheetProducts: TwinDictionary[] = []
            flushSync(async () => {
                await setCompleteData((old) => {
                    if (!old) {
                        return null
                    }
                    const copy = JSON.parse(JSON.stringify(old))
                    const items = copy.tableData
                    const activeIndex = active.data?.current?.myIndex
                    const overIndex = over.data?.current?.myIndex
                    const myMoveItems = items.slice(activeIndex, activeIndex + (activeNChildren || 0) + 1)
                    items.splice(activeIndex, myMoveItems.length)
                    let myOverIndex = overIndex
                    if (overIndex > activeIndex) {
                        if (over.data?.current?.lastPosition) {
                            myOverIndex = over.data?.current?.lastPosition - myMoveItems.length + 1
                        } else {
                            myOverIndex = overIndex - myMoveItems.length + 1
                        }
                    }
                    items.splice(myOverIndex, 0, ...myMoveItems)
                    let nLine = 1
                    let inventPosition = 0
                    copy.dictPosTableData = {}
                    for (const element of items) {
                        element.absoluteIndex = inventPosition
                        if (element.WorkSheetId && element.id) {
                            if (element.nLine !== nLine) {
                                element.nLine = nLine
                                worksheetProducts.push({ nLine: element.nLine, id: element.id, WorkSheetId: element.WorkSheetId, ProductId: element.ProductId, taxUsed: element.taxUsed })
                            }
                            nLine++
                        }
                        element.inventPosition = inventPosition
                        copy.dictPosTableData[element.id] = inventPosition
                        inventPosition++
                    }
                    return { ...copy, tableData: items }
                })
                setActiveNChildren(null)
                await twinFetchPostJSON(inputTableURLs.updateOrderLines, { worksheetProducts })
            })
        }
    }, [setCompleteData, activeNChildren, inputTableURLs.updateOrderLines])

    const containerRenderer: TableWithoutVirtualListingContainerRenderer = useCallback((props) => {
        const result: JSX.Element[] = []
        const resultIds: any[] = []
        for (const key in props.children) {
            const element = props.children[key]
            const dataElement = element?.props?.rowData
            let top = element.props.style.top
            element.props.style.top = undefined
            element.props.style.position = undefined
            result.push(<ITSortableItem id={dataElement?.id} key={dataElement?.id} data={{ nChildren: 0, myIndex: dataElement?.inventPosition }} className='flex-col' style={{ ...element?.props?.style || {}, top, position: 'absolute' }}>{element}</ITSortableItem>)
            resultIds.push({ id: dataElement?.id })
        }
        return (
            <DndContext sensors={sensors} collisionDetection={closestCenter}
                onDragEnd={handleOnDragEnd} onDragStart={({ active }) => {
                    setActiveNChildren(active?.data?.current?.nChildren)
                }} onDragCancel={() => {
                    setActiveNChildren(null);
                }}>
                <SortableContext strategy={verticalListSortingStrategy} items={resultIds}>
                    <div className='data_rows h-full' style={props.style}>{result}</div>
                </SortableContext>
            </DndContext>
        )
    }, [sensors, handleOnDragEnd, setActiveNChildren])

    const addCustomRender = useCallback((columns: ITComponentElement<any>[]) => {
        let result: ColumnConfiguration[] = []
        const onChanges: OnChangeElements = {
            onChange: onChange,
            onChangeFetch: onChangeFetch,
        }
        if (components) {
            let findFirstNameColumn = false
            for (const key in columns) {
                let column = columns[key]
                const myElement = column as ColumnConfiguration
                if (column.component) {
                    const Element = components[column.component]
                    const onChange = onChanges[column?.typeOnChange || 'onChangeFetch']
                    if (column.component === 'ITDeleteRow') {
                        let MyDeleteComponent = ITDeleteRow
                        if (components['DeleteComponent']) {
                            MyDeleteComponent = components['DeleteComponent']
                        }
                        myElement.customRender = (parameterValue: any, allRowData: any) => <MyDeleteComponent key={'delete' + parameterValue} {...{ parameterValue, allRowData }} onClick={(id, allRowData) => id ? setOpenModal({ type: 'deleteLine', allRowData }) : deleteLine(allRowData.id)} />
                    } else if (column.component === 'ITCheckbox') {
                        const originalLength = completeData?.tableData?.length || 0
                        const myTableDataLength = ((filteredTableDataLength ? filteredTableDataLength : originalLength) || 0)
                        const isChecked = selectedLength > 0 && selectedLength === myTableDataLength
                        const disabled = column?.extraProps?.disabled || false
                        const nextStepNotFinished = column?.extraProps?.nextStepNotFinished || false
                        column.dataKey = isChecked ? 'selected_full' : 'selected_not_full'
                        myElement.label = () => <Checkbox label={''} onChange={selectAllLines} checked={isChecked} disabled={disabled} />
                        let MyCheckboxComponent = components['CheckboxComponent']
                        myElement.customRender = (parameterValue: any, allRowData: any) => <MyCheckboxComponent key={'select' + parameterValue} {...{ parameterValue, allRowData, nextStepNotFinished }} value={allRowData?.selected === true} onChange={(checked: boolean) => changeSelectedOneLine?.(checked, allRowData?.absoluteIndex || allRowData?.rowIndex)} />
                    } else {
                        let extraData: TwinDictionary = {}
                        if ((myElement.id === 'name' || myElement.id === 'referenceAndName' || myElement.id === 'reference' || myElement.id === 'code_bar') && !findFirstNameColumn) {
                            extraData.findFirstNameColumn = true
                            findFirstNameColumn = true
                        }
                        myElement.customRender = (value, allRowData) => <Element {...column as any} {...{ value, allRowData, onChange, CurrencyId, setOpenModal, ...extraData }} />
                    }
                }
                result.push(myElement)
            }
        }
        return result

    }, [CurrencyId, onChange, setOpenModal, deleteLine, components, onChangeFetch, changeSelectedOneLine,  filteredTableDataLength, selectAllLines, selectedLength, completeData?.tableData?.length])

    const parseColumns = useCallback((columns: ColumnITLayoutTableConfiguration[]) => {
        let result = addCustomRender(columns as any)
        return result
    }, [addCustomRender])

    const parsedColumns = useMemo(() => parseColumns(columns), [columns, parseColumns])

    return { parsedColumns, rowRenderer: rowRenderer || handleProductLine, setOpenModal, openModal, eventFunctions: { deleteLine, onChange, onChangeFetch, changeSingleField }, containerRenderer, onChangeSearchBar, filters: completeData.filters }
}